import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Icon } from '@eti/components';
import { useAuth } from '@eti/authentication';
import { useSiteContext, useTranslation, useProperty } from '@eti/providers';
import { css, etiColors } from '@eti/styles';
import { AuthenticationType, LoginSource } from '@eti/schema-types';
import { asyncInit } from '../../../utils/facebook';
import { CONNECTED } from '../constants';
import { CRITICAL, INFO } from '../utils/messageTypes';
import { scrollToElement } from '../../../utils/scrollToElement';
import { routes } from '../../../constants/routesAndApisConstants';

const fbColor = '#1877f2';

const facebookButtonStyles = css`
  align-items: center;
  background-color: ${etiColors.white};
  border: 1px solid ${fbColor};
  border-radius: 4px;
  color: ${fbColor};
  cursor: pointer;
  display: flex;
  font-family: 'Helvetica', 'Arial', sans-serif;
  font-weight: 700;
  gap: 8px;
  height: 40px;
  justify-content: start;
  letter-spacing: 0.25px;
  max-width: 296px;
  min-width: 296px;
  padding: 0 12px;

  & span {
    flex-grow: 1;
  }

  &:hover,
  &:focus {
    background-color: #f7fafe;
  }
`;

interface FacebookProps {
  callback?: () => void;
  setNotification: Dispatch<SetStateAction<{ message: string; type: string; dataTestId: string }>>;
}

const Facebook = ({ callback, setNotification }: FacebookProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { authenticateUser } = useAuth();
  const clientID = p('IbeClient.MyBookings.Facebook.Client.Id');

  const history = useHistory();
  const {
    language: { code: languageCode },
    market: { code: marketCode },
  } = useSiteContext();
  const [isSdkLoaded, setIsSdkLoaded] = useState(false);

  useEffect(() => {
    asyncInit(clientID, languageCode, marketCode, () => {
      setIsSdkLoaded(true);
    });
  }, [clientID, languageCode, marketCode]);

  const authenticate = useCallback(
    async (token) => {
      const authRequest = {
        email: null,
        orderNumber: null,
        authenticationType: AuthenticationType.Facebook,
        token,
        loginSource: LoginSource.OrderLogin,
      };
      const { data } = await authenticateUser(authRequest);
      const authenticateUserData = data?.authenticateUser;

      return authenticateUserData?.authenticated;
    },
    [authenticateUser],
  );

  const handleResponse = useCallback(
    (response) => {
      const processResponse = async () => {
        if (!response.authResponse) {
          return;
        }

        if (!response.authResponse.grantedScopes.includes('email')) {
          setNotification({
            message: t('Postbooking.Login.Facebook.Email.Permission.Error'),
            type: INFO,
            dataTestId: 'facebook-email-permission-info-message',
          });
          scrollToElement('#loginMessage');
          return;
        }

        try {
          const authenticated = await authenticate(response.authResponse.accessToken);
          if (authenticated) {
            if (callback) {
              callback();
            } else {
              history.push(routes.ORDER_LIST);
            }
          } else {
            setNotification({
              message: t('Postbooking.Login.Internal.Error'),
              type: CRITICAL,
              dataTestId: 'login-service-error-message',
            });
            scrollToElement('#loginMessage');
          }
        } catch (error) {
          setNotification({
            message: t('Postbooking.Login.Internal.Error'),
            type: CRITICAL,
            dataTestId: 'login-service-error-message',
          });
          scrollToElement('#loginMessage');
        }
      };

      return processResponse();
    },
    [authenticate, callback, history, setNotification, t],
  );

  const handleLogin = () => {
    if (!isSdkLoaded) {
      return;
    }

    window.FB.getLoginStatus((response) => {
      if (response.status === CONNECTED && response.authResponse.grantedScopes?.includes('email')) {
        handleResponse(response).catch(() => {});
      } else {
        window.FB.login(handleResponse, {
          scope: 'email',
          return_scopes: true,
          auth_type: 'rerequest',
        });
      }
    });
  };

  return (
    <button
      className={facebookButtonStyles}
      data-testid="fb-button"
      onClick={handleLogin}
      type="button"
    >
      <Icon color={fbColor} id="fb-dark" size="20px" />
      <span>{t('Postbooking.Login.Facebook.Button.Label')}</span>
    </button>
  );
};

export default Facebook;
